import {
  Menu,
  MenuButton,
  IconButton,
  Portal,
  MenuList,
  MenuItem,
  Button,
} from '@chakra-ui/react';
import { CompositionGroup } from '@texas/api/endpoints/compositionGroup/compositionGroupApi';
import { Icons, defaultIconSize } from '@texas/components/shared/Icons';
import { useTranslation } from 'react-i18next';
import { CopyProps } from './GroupedVersions';

export type menuButtonVariant = 'icon' | 'outlined';

export function VersionsMenu({
  version,
  basedOnGroupVersion,
  menuButtonVariant = 'icon',
  onRestoreOpen,
  onArchiveOpen,
  onOpen,
  onCopyOpen,
  onHistoryOpen,
  onPatch,
  onSetGarpDataOpen,
  onViewLayout,
  onInitiateSampling,
  patchCount,
  loading,
  children,
}: {
  version: CompositionGroup;
  basedOnGroupVersion: number;
  menuButtonVariant?: menuButtonVariant;
  onRestoreOpen?: (id: number) => void;
  onArchiveOpen?: (id: number) => void;
  onOpen: (id: number, readOnly: boolean, isArchived: boolean) => void;
  onCopyOpen?: (props: CopyProps) => void;
  onHistoryOpen: (id: number, name: string) => void;
  onPatch?: (id: number) => void;
  onSetGarpDataOpen?: () => void;
  onViewLayout?: (fileId: number) => void;
  onInitiateSampling?: (groupIds: number[]) => void;
  patchCount?: number;
  loading?: boolean;
  children?: React.ReactNode;
}) {
  if (menuButtonVariant === 'outlined') {
    return (
      <Menu isLazy={true}>
        <MenuButton
          as={Button}
          rightIcon={<Icons.ChevronDown />}
          h="auto"
          w="100%"
          py={2}
          textAlign="start"
          variant="texas-outline-light"
          isLoading={loading}
        >
          {children}
        </MenuButton>
        <MenuListPortal
          version={version}
          basedOnGroupVersion={basedOnGroupVersion}
          onRestoreOpen={onRestoreOpen}
          onArchiveOpen={onArchiveOpen}
          onOpen={onOpen}
          onCopyOpen={onCopyOpen}
          onHistoryOpen={onHistoryOpen}
          onPatch={onPatch}
          patchCount={patchCount}
          onSetGarpDataOpen={onSetGarpDataOpen}
          onViewLayout={onViewLayout}
          onInitiateSampling={onInitiateSampling}
        />
      </Menu>
    );
  }

  return (
    <Menu isLazy={true}>
      <MenuButton
        as={IconButton}
        size="sm"
        icon={<Icons.DotsHorizontal boxSize={defaultIconSize} />}
        aria-label="show more"
        onClick={(e) => {
          e.stopPropagation();
        }}
        isLoading={loading}
      />
      <MenuListPortal
        version={version}
        basedOnGroupVersion={basedOnGroupVersion}
        onRestoreOpen={onRestoreOpen}
        onArchiveOpen={onArchiveOpen}
        onOpen={onOpen}
        onCopyOpen={onCopyOpen}
        onHistoryOpen={onHistoryOpen}
        onPatch={onPatch}
        patchCount={patchCount}
        onSetGarpDataOpen={onSetGarpDataOpen}
        onViewLayout={onViewLayout}
        onInitiateSampling={onInitiateSampling}
      />
    </Menu>
  );
}

function MenuListPortal({
  version,
  basedOnGroupVersion,
  onRestoreOpen,
  onArchiveOpen,
  onOpen,
  onCopyOpen,
  onHistoryOpen,
  onPatch,
  onSetGarpDataOpen,
  onViewLayout,
  onInitiateSampling,
  patchCount,
}: {
  version: CompositionGroup;
  basedOnGroupVersion: number;
  onRestoreOpen?: (id: number) => void;
  onArchiveOpen?: (id: number) => void;
  onOpen: (id: number, readOnly: boolean, isArchived: boolean) => void;
  onCopyOpen?: (props: CopyProps) => void;
  onHistoryOpen: (id: number, name: string) => void;
  onPatch?: (id: number) => void;
  onSetGarpDataOpen?: () => void;
  onViewLayout?: (fileId: number) => void;
  onInitiateSampling?: (groupIds: number[]) => void;
  patchCount?: number;
}) {
  const { t } = useTranslation();

  if (version.readOnly)
    return (
      <Portal>
        <MenuList>
          <MenuItem
            onClick={(e) => {
              e.stopPropagation();
              onOpen(version.id, version.readOnly, version.archived !== null);
            }}
            icon={<Icons.OpenInNew boxSize={defaultIconSize} />}
          >
            {t('compositionGroup.version.view')}
          </MenuItem>
          <MenuItem
            onClick={(e) => {
              e.stopPropagation();
              onHistoryOpen(
                version.id,
                t('compositionGroup.version.versionNrOption', {
                  nr: version.version,
                  optionNr: basedOnGroupVersion,
                }),
              );
            }}
            icon={<Icons.Clock boxSize={defaultIconSize} />}
          >
            {t('general.history.viewHistory')}
          </MenuItem>
          {onPatch && !!patchCount && patchCount > 0 && (
            <MenuItem
              onClick={(e) => {
                e.stopPropagation();
                onPatch(version.id);
              }}
              icon={<Icons.Plus boxSize={defaultIconSize} />}
            >
              {t('compositionGroup.version.addVersionToCells', {
                count: patchCount,
              })}
            </MenuItem>
          )}
          {onSetGarpDataOpen && version.cellX === null && (
            <MenuItem
              icon={<Icons.BreifcaseArrowLeftRight boxSize="4" />}
              aria-label={t('garp.fillData')}
              onClick={onSetGarpDataOpen}
            >
              {t('garp.fillData')}
            </MenuItem>
          )}
        </MenuList>
      </Portal>
    );

  return (
    <Portal>
      <MenuList>
        {version.archived ? (
          <>
            <MenuItem
              onClick={(e) => {
                e.stopPropagation();
                onOpen(version.id, version.readOnly, true);
              }}
              icon={<Icons.OpenInNew boxSize={defaultIconSize} />}
            >
              {t('compositionGroup.version.view')}
            </MenuItem>
            {onRestoreOpen && (
              <MenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  onRestoreOpen(version.id);
                }}
                icon={<Icons.Undo boxSize={defaultIconSize} />}
              >
                {t('compositionGroup.version.restoreVersion')}
              </MenuItem>
            )}
          </>
        ) : (
          <>
            {!version.productApprovals?.length && (
              <>
                <MenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpen(version.id, version.readOnly, false);
                  }}
                  icon={<Icons.Pencil boxSize={defaultIconSize} />}
                >
                  {t('compositionGroup.version.edit')}
                </MenuItem>
              </>
            )}
            {onArchiveOpen && (
              <MenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  onArchiveOpen(version.id);
                }}
                icon={<Icons.ArchiveOutline boxSize={defaultIconSize} />}
              >
                {t('compositionGroup.version.archiveVersion')}
              </MenuItem>
            )}
          </>
        )}
        {onCopyOpen && (
          <MenuItem
            onClick={(e) => {
              e.stopPropagation();
              onCopyOpen({
                groupId: version.id,
                supplierId: version.supplierId!,
                branchId: version.branchId!,
                cellX: version.cellX,
                cellY: version.cellY,
                replaceId: null,
                basedOnId: version.basedOnGroupId,
              });
            }}
            icon={<Icons.ContentCopy boxSize={defaultIconSize} />}
          >
            {t('compositionGroup.version.copyIntoNew')}
          </MenuItem>
        )}

        {onCopyOpen &&
          version.basedOnGroupId &&
          !version.archived &&
          !version.productApprovals?.length &&
          !version.controlledById && (
            <MenuItem
              onClick={(e) => {
                e.stopPropagation();
                onCopyOpen({
                  groupId: version.basedOnGroupId!,
                  supplierId: version.supplierId!,
                  branchId: version.branchId!,
                  cellX: version.cellX,
                  cellY: version.cellY,
                  replaceId: version.id,
                  basedOnId: version.basedOnGroupId,
                });
              }}
              icon={<Icons.Refresh boxSize={defaultIconSize} />}
            >
              {t('compositionGroup.version.resetToOption')}
            </MenuItem>
          )}
        {onPatch && !!patchCount && patchCount > 0 && (
          <MenuItem
            onClick={(e) => {
              e.stopPropagation();
              onPatch(version.id);
            }}
            icon={<Icons.Plus boxSize={defaultIconSize} />}
          >
            {t('compositionGroup.version.addVersionToCells', {
              count: patchCount,
            })}
          </MenuItem>
        )}
        <MenuItem
          onClick={(e) => {
            e.stopPropagation();
            onHistoryOpen(
              version.id,
              t('compositionGroup.version.versionNrOption', {
                nr: version.version,
                optionNr: basedOnGroupVersion,
              }),
            );
          }}
          icon={<Icons.Clock boxSize={defaultIconSize} />}
        >
          {t('general.history.viewHistory')}
        </MenuItem>
        {onSetGarpDataOpen && (
          <MenuItem
            icon={<Icons.BreifcaseArrowLeftRight boxSize="4" />}
            aria-label={t('garp.fillData')}
            onClick={onSetGarpDataOpen}
          >
            {t('garp.fillData')}
          </MenuItem>
        )}
        {onViewLayout && version.fileId && (
          <MenuItem
            icon={<Icons.PencilRuler boxSize={defaultIconSize} />}
            onClick={(e) => {
              e.stopPropagation();
              onViewLayout(version.fileId!);
            }}
          >
            {t('compositionGroup.version.viewLayout')}
          </MenuItem>
        )}
        {onInitiateSampling &&
          version.controlledGroupIds.length > 0 &&
          !version.archived && (
            <MenuItem
              icon={<Icons.Gift boxSize={defaultIconSize} />}
              onClick={(e) => {
                e.stopPropagation();
                onInitiateSampling(version.controlledGroupIds);
              }}
            >
              {t('sample.initate')}
            </MenuItem>
          )}
      </MenuList>
    </Portal>
  );
}
